<template>
  <div class="flex flex-col">
    <div class="grow flex flex-col px-4 py-8">
      <FormKit
          type="form"
          id="selectdate"
          name="SelectDate"
          submit-label="Continue"
          @submit="submitHandler"
          :actions="false"
      >
        <div class="content select-date">
          <FormKit
              type="datepicker"
              name="appointmentdate"
              label="Select date"
              placeholder="Select date"
              picker-only
              week-start="0"
              :classes="{

            outer: {
              'max-h-[100px]': true,
              'custom-date-picker': true,
            },
          }"
              :min-date="minDate"
              :max-date="maxDate"
              :disabled-days="disabledDays"
              v-model="selectedDate"
              @input="dateChanged"
              popover
              overlay

          >
            <template #day="{ classes, day, fns, minDate, maxDate }">
              <div :class="classes.day">
                {{ day.getDate() }}
                <div
                    v-if="!fns.isDisabled(day) && day > minDate && day < maxDate"
                    class="disabled-indicator"
                ></div>
              </div>
            </template>
          </FormKit>
          <div class="mt-[44px]" v-if="transformedSlots.length">
            <FormKit
                type="togglebuttons"
                name="timeslot"
                label="Select time slot"
                :options="transformedSlots"
                v-model="selectedSlot"
                :config="{
                    classes: timeSlotClasses,
                  }"
                validation="required"
                vertical>
              <template #default="context">
                <div class="flex items-center justify-between">
                  <span class="text-base text-darkGrey">{{ context.option.label }}</span>
                  <span class="text-sm text-grey-700">{{ context.option.available }}</span>
                </div>
              </template>
            </FormKit>
          </div>
        </div>
      </FormKit>
    </div>
    <Buttons>
      <Button class="btn btn__primary" :disabled="isSubmitDisabled" :loading="isLoading"
              @click="submitHandler">
        Continue
      </Button>
    </Buttons>
  </div>
</template>

<script setup>
import {ref, watch, watchEffect, onMounted} from "vue";
import {FormKit} from "@formkit/vue";
import {useFMStore} from "@/store/FMStore";
import {addDay, format} from "@formkit/tempo";
import {computeBoundsFromSlots, shouldDisableDate, toUnavailableSet} from "~/utils/calendar";
import Button from "~/components/ui/Button.vue";
import Buttons from "~/components/ui/Buttons.vue";


const selectedDate = ref();
const selectedDateFormated = ref();
const FMStore = useFMStore();
let unavailableDates = [];
let unavailableSet = new Set();
const filteredSlots = ref([]);
const transformedSlots = ref([]);
const selectedSlot = ref();
const isSubmitDisabled = computed(() => !selectedSlot.value);
const minDate = ref();
const maxDate = ref();
const isLoading = ref(false);
const disabled = ref(true);
const availableDatesSet = ref(new Set());

onMounted(async () => {
  await FMStore.getAvailability();
  await FMStore.getBlackoutSchedule();
  unavailableDates = await FMStore.getUnavailableDates();
  unavailableSet = toUnavailableSet(unavailableDates);

  const bounds = computeBoundsFromSlots(FMStore.openTimeSlots || []);
  minDate.value = bounds.minDate;
  maxDate.value = bounds.maxDate;
  console.log("minDate set to:", minDate.value);
  console.log("maxDate set to:", maxDate.value);

  // Build a set of available calendar days from time slots for the selected area
  const area = FMStore.selectedWorkOrder?.Area;
  const keys = (FMStore.openTimeSlots || [])
      .filter(s => !area || s.Area === area || s.Area === 'ALL')
      .map(s => String(s.TimeSlotStart).split('T')[0])
      .filter(Boolean);
  availableDatesSet.value = new Set(keys);

  selectedDate.value = minDate.value;
  selectedDateFormated.value = format(new Date(selectedDate.value), "YYYY-MM-DD");
  FMStore.selectedDate = selectedDateFormated.value;
  console.log("Selected Date Formatted:", selectedDateFormated.value);
});

function disabledDays(node, date) {
  // First, apply global min/max and blackout constraints
  if (shouldDisableDate(date, minDate.value, maxDate.value, unavailableSet)) return true;
  // Then, only allow dates that have at least one slot
  const key = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      .toISOString()
      .split('T')[0];
  return !availableDatesSet.value.has(key);
}

function dateChanged(newDate) {
  selectedDate.value = newDate;
  if (selectedDate.value) {
    selectedDateFormated.value = format(
        new Date(selectedDate.value),
        "YYYY-MM-DD"
    );
    FMStore.selectedDate = selectedDateFormated.value;
  }
}

watch(selectedDate, (newValue) => {
  if (newValue) {
    selectedDateFormated.value = format(new Date(newValue), "YYYY-MM-DD");
  }
});

async function submitHandler() {
  FMStore.selectedTimeSlot = selectedSlot.value;
  FMStore.stepNumber++;
  FMStore.goToStep('Confirm');
}

function transformOptions(slots) {
  return slots.map((slot) => ({
    label: `${formatUTCTimeOnly(slot.TimeSlotStart)} - ${formatUTCTimeOnly(slot.TimeSlotEnd)}`,
    available: `Available: ${slot.Available}`,
    value: slot.Id,
  }));
}

function filterByDate(date, area, slots) {
  if (!slots) {
    console.error("Slots data is undefined or null");
    return [];
  }
  return slots.filter((slot) => {
    const slotDate = slot.TimeSlotStart.split("T")[0];
    return slotDate === date && slot.Area === area;
  });
}
watchEffect(() => {
  try {
    const selectedDate = FMStore.selectedDate;
    const selectedArea = FMStore.selectedWorkOrder?.Area;
    const openSlots = FMStore.openTimeSlots;

    if (!selectedDate || !selectedArea || !openSlots) {
      return;
    }
    const slots = filterByDate(selectedDate, selectedArea, openSlots);
    filteredSlots.value = slots;
    transformedSlots.value = transformOptions(slots);
    console.log("Updated Slots for:", selectedDate, transformedSlots.value);
  } catch (error) {
    console.error("Error during watchEffect:", error);
  }
});

function formatUTCTimeOnly(timestamp) {
  const date = new Date(timestamp);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes} ${period}`;
}

const timeSlotClasses = {
  options: 'w-full',
  inputInner: {
    'w-full': true
  },
  input: {
    'ring-blue-400': false,
    'bg-white': true,
    'text-blue-500': false,
    '!px-[16px]': true,
    '!py-2.5': true,
    'border-blue-500': false,
    'border-[#B1B5B9]': true,
    'font-medium': true,
    'hover:!bg-[#207ED514]': true,
    "group-data-[vertical=false]/options:group-first/option:!rounded-2xl": false,
    "group-data-[vertical=true]/options:group-first/option:!rounded-2xl": false,
    "group-data-[vertical=false]/options:group-first/option:!rounded-tr-none": false,
    "group-data-[vertical=false]/options:group-first/option:!rounded-br-none": false,
    "group-data-[vertical=true]/options:group-first/option:!rounded-b-none": false,
    "group-data-[vertical=true]/options:group-first/option:!rounded-br-none": false,
    "group-data-[vertical=false]/options:group-last/option:!rounded-2xl": false,
    "group-data-[vertical=true]/options:group-last/option:!rounded-2xl": false,
    "group-data-[vertical=false]/options:group-last/option:!rounded-tl-none": false,
    "group-data-[vertical=false]/options:group-last/option:!rounded-bl-none": false,
    "group-data-[vertical=true]/options:group-last/option:!rounded-tl-none": false,
    "group-data-[vertical=true]/options:group-last/option:!rounded-tr-none": false,
    "group-data-[vertical=true]/options:group-last/option:!rounded-bl-[6px]": true,
    "group-data-[vertical=true]/options:group-last/option:!rounded-br-[6px]": true,
    "group-data-[vertical=true]/options:group-first/option:!rounded-t-[6px]": true,
    "group-data-[vertical=false]/options:group-[]/option:aria-[pressed=true]:border-x-blue-500": false,
    "group-data-[vertical=false]/options:group-first/option:aria-[pressed=true]:border-l-blue-500": false,
    "group-data-[vertical=false]/options:group-last/option:aria-[pressed=true]:border-r-blue-500": false,
    "dark:group-data-[vertical=false]/options:group-[]/option:aria-[pressed=true]:border-x-blue-500": false,
    "group-data-[vertical=true]/options:group-[]/option:aria-[pressed=true]:border-y-blue-500": false,
    "group-data-[vertical=true]/options:group-first/option:aria-[pressed=true]:border-t-blue-500": false,
    "group-data-[vertical=true]/options:group-last/option:aria-[pressed=true]:border-b-blue-500": false,
    "dark:group-data-[vertical=true]/options:group-[]/option:aria-[pressed=true]:border-y-blue-500": false,
    "dark:group-data-[vertical=true]/options:group-first/option:aria-[pressed=true]:border-t-blue-500": false,
    "dark:group-data-[vertical=true]/options:group-last/option:aria-[pressed=true]:border-b-blue-500": false,
    "aria-[pressed=true]:bg-blue-500": false,
    "aria-[pressed=true]:bg-[#207ED533] hover:!bg-[#207ED533]": true,
    "aria-[pressed=true]:!font-bold": true,
    "aria-[pressed=true]:item_pressed": true,

  }
}
</script>
<style>
button[aria-pressed="true"] span {
  color: var(--dark-grey) !important;
}
</style>

